(() => {
  // EcoBorder pack planner. Each piece is 4 ft and is cut only at the 1 ft marks, so every
  // section rounds up to whole feet. Full pieces cover each section; the 1–3 ft remainders are
  // cut from shared pieces (first-fit decreasing, optimal for sizes 1–3 in a 4 ft piece).
  // All arithmetic is integer (BigInt) so no floating-point overcounts.
  const PIECE_FT = 4n;

  function parseFeet(raw, decimalMark) {
    let s = String(raw ?? '').trim();
    if (decimalMark === ',') {
      if (s.indexOf('.') !== -1) return null;
      s = s.replace(',', '.');
    }
    const match = /^(\d+)(?:\.(\d+))?$/.exec(s);
    if (!match) return null;
    const frac = match[2] || '';
    let n;
    try {
      n = BigInt(match[1] + frac);
    } catch (error) {
      return null;
    }
    if (n <= 0n) return null;
    return { n, d: 10n ** BigInt(frac.length) };
  }

  // Whole feet a section needs (pieces are cut at 1 ft marks): ceil(n / d).
  function wholeFeet(raw, decimalMark) {
    const p = parseFeet(raw, decimalMark);
    if (!p) return null;
    return (p.n + p.d - 1n) / p.d;
  }

  // sections: array of raw strings. piecesPerPack: 6 (L) or 4 (Curb).
  function plan(sections, piecesPerPack, decimalMark) {
    let ppp;
    try {
      ppp = BigInt(piecesPerPack);
    } catch (error) {
      return null;
    }
    if (ppp <= 0n) return null;
    const rows = [];
    const remainders = [];
    let full = 0n;
    (sections || []).forEach((raw, index) => {
      if (String(raw ?? '').trim() === '') return;
      const ft = wholeFeet(raw, decimalMark);
      if (ft == null) {
        rows.push({ index, invalid: true });
        return;
      }
      const f = ft / PIECE_FT;
      const cut = ft % PIECE_FT;
      full += f;
      if (cut > 0n) remainders.push(cut);
      rows.push({ index, feet: ft, full: f, cut });
    });
    const valid = rows.filter((r) => !r.invalid);
    if (!valid.length) return null;
    remainders.sort((a, b) => (a > b ? -1 : a < b ? 1 : 0));
    const bins = [];
    remainders.forEach((r) => {
      const i = bins.findIndex((space) => space >= r);
      if (i === -1) bins.push(PIECE_FT - r);
      else bins[i] -= r;
    });
    const pieces = full + BigInt(bins.length);
    const packs = (pieces + ppp - 1n) / ppp;
    return { rows, pieces, cutPieces: BigInt(bins.length), packs, spare: packs * ppp - pieces };
  }

  const fill = (template, values) =>
    Object.keys(values).reduce((s, k) => s.split(`__${k}__`).join(String(values[k])), String(template || ''));

  function sectionInputs(root) {
    return Array.from(root.querySelectorAll('[data-eco-feet]'));
  }

  const pick = (n, pair) => {
    const [one, other] = String(pair || '').split('|');
    return n === 1n ? one : (other ?? one);
  };

  function paint(root) {
    const line = root.querySelector('[data-eco-line]');
    const list = root.querySelector('[data-eco-rows]');
    const note = root.querySelector('[data-eco-note]');
    if (!line) return;
    const inputs = sectionInputs(root);
    const result = plan(inputs.map((i) => i.value), root.dataset.piecesPerPack, root.dataset.decimal);
    if (list) list.textContent = '';
    if (!result) {
      line.textContent = root.dataset.hint || '';
      if (note) note.textContent = '';
      return;
    }
    if (list) {
      result.rows.forEach((r) => {
        const li = document.createElement('li');
        const n = r.index + 1;
        const d = root.dataset;
        if (r.invalid) li.textContent = fill(d.rowInvalid, { N: n });
        else {
          const v = { N: n, FEET: r.feet, FEETUNIT: pick(r.feet, d.wFt), FULL: r.full, FULLWORD: pick(r.full, d.wFull), CUT: r.cut, CUTUNIT: pick(r.cut, d.wFt) };
          if (r.full === 0n) li.textContent = fill(d.rowCutOnly, v);
          else li.textContent = fill(r.cut > 0n ? d.rowCut : d.rowFull, v);
        }
        list.appendChild(li);
      });
    }
    const d = root.dataset;
    const bad = result.rows.find((r) => r.invalid);
    if (bad) {
      line.textContent = fill(d.rowInvalid, { N: bad.index + 1 });
      if (note) note.textContent = '';
      return;
    }
    line.textContent = fill(d.summary, {
      PIECES: result.pieces,
      PIECEWORD: pick(result.pieces, d.wPiece),
      PACKS: result.packs,
      PACKWORD: pick(result.packs, d.wPack),
      PER: d.piecesPerPack,
      SPARE: result.spare,
      SPAREWORD: pick(result.spare, d.wSpare),
    });
    if (note) note.textContent = fill(root.dataset.note, { PER: root.dataset.piecesPerPack });
  }

  function addSection(root) {
    const wrap = root.querySelector('[data-eco-sections]');
    const tpl = root.querySelector('template[data-eco-section-template]');
    if (!wrap || !tpl) return;
    const count = sectionInputs(root).length;
    if (count >= 12) return;
    const n = count + 1;
    const frag = tpl.content.cloneNode(true);
    const row = frag.querySelector('[data-eco-section]');
    const label = frag.querySelector('label');
    const input = frag.querySelector('[data-eco-feet]');
    root.ecoSeq = (root.ecoSeq || 1) + 1;
    const id = `${root.dataset.uid || 'EcoFeet'}-s${root.ecoSeq}`;
    input.id = id;
    label.setAttribute('for', id);
    label.textContent = fill(root.dataset.sectionLabel, { N: n });
    const remove = frag.querySelector('[data-eco-remove]');
    if (remove) remove.setAttribute('aria-label', fill(root.dataset.removeLabel, { N: n }));
    wrap.appendChild(frag);
    const added = wrap.lastElementChild;
    if (added && added.querySelector) {
      const field = added.querySelector('[data-eco-feet]');
      if (field) field.focus();
    }
    renumber(root);
    paint(root);
  }

  function renumber(root) {
    root.querySelectorAll('[data-eco-section]').forEach((row, i) => {
      const n = i + 1;
      const label = row.querySelector('label');
      const remove = row.querySelector('[data-eco-remove]');
      if (label) label.textContent = fill(root.dataset.sectionLabel, { N: n });
      if (remove) remove.setAttribute('aria-label', fill(root.dataset.removeLabel, { N: n }));
    });
  }

  function initEcoPackCalc(root) {
    if (!root || root.dataset.ecoCalcInit === '1') return;
    if (!root.querySelector('[data-eco-feet]')) return;
    root.dataset.ecoCalcInit = '1';
    root.addEventListener('input', (event) => {
      if (event.target && event.target.matches && event.target.matches('[data-eco-feet]')) paint(root);
    });
    root.addEventListener('click', (event) => {
      const t = event.target && event.target.closest ? event.target.closest('[data-eco-add],[data-eco-remove]') : null;
      if (!t || !root.contains(t)) return;
      event.preventDefault();
      if (t.hasAttribute('data-eco-add')) {
        addSection(root);
        return;
      }
      const row = t.closest('[data-eco-section]');
      if (row) row.remove();
      renumber(root);
      const first = root.querySelector('[data-eco-feet]');
      if (first) first.focus();
      paint(root);
    });
    const add = root.querySelector('[data-eco-add]');
    if (add) add.hidden = false;
    paint(root);
  }

  function scan(scope) {
    const base = scope && scope.querySelectorAll ? scope : document;
    if (base.id === 'eco-pack-calc') initEcoPackCalc(base);
    const found = base.querySelector ? base.querySelector('#eco-pack-calc') : null;
    if (found && found !== base) initEcoPackCalc(found);
  }

  if (!window.ecoPackCalcBound) {
    window.ecoPackCalcBound = true;
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  }
  window.ecoPackCalc = { parseFeet, wholeFeet, plan, initEcoPackCalc };
  scan(document);
})();
